// Händelser och spår (admin mockup §6): the ledger itself, read-only, and the trace of one
// container — its label strip, contents and every event that touched it.

import { useEffect, useState } from "react";
import { Admin, printAndTell, Api, type ContainerSummary, type HistoryEvent, type LedgerEvent, type ReferenceBundle } from "../api/client.js";
import { fmtQty, t } from "../i18n.js";
import { DetailBar, useDetailHistory } from "./settings-ui.jsx";

const fmt = (iso: string) => new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "medium" });

let locationCodes = new Map<string, string>();
const loc = (id: unknown) => (typeof id === "string" ? locationCodes.get(id) ?? `${id.slice(0, 8)}…` : "—");

function subjectOf(e: { type: string; payload: Record<string, unknown> }): string {
  const p = e.payload as Record<string, string | string[] | null | undefined>;
  if (e.type === "received") return `${p["kind"] === "logistic-unit" ? t("unit.pallet") : t("unit.kolli")} ${p["sscc"] ?? p["gtin"] ?? ""}${p["lot"] && typeof p["lot"] === "object" ? ` · ${(p["lot"] as { number?: string }).number ?? ""}` : ""} → ${loc(p["locationId"])}`;
  if (e.type === "moved") return `${(p["containerIds"] as string[] | undefined)?.length ?? 1} → ${loc(p["toLocationId"])}${p["proposedLocationId"] && p["proposedLocationId"] !== p["toLocationId"] ? ` (föreslagen ${loc(p["proposedLocationId"])})` : ""}`;
  if (e.type === "aggregated") return `${(p["childContainerIds"] as string[] | undefined)?.length ?? 0} kolli → ${String(p["parentContainerId"] ?? "").slice(0, 8)}…`;
  if (e.type === "inspected") return `${p["disposition"] ?? ""} · ${p["detail"] ?? ""}`;
  return "";
}

export function Events() {
  const [events, setEvents] = useState<LedgerEvent[] | null>(null);
  const [integrity, setIntegrity] = useState<{ ok: boolean; eventsChecked: number } | null>(null);
  const [query, setQuery] = useState("");
  const [trace, setTrace] = useState<{ container: Awaited<ReturnType<typeof Admin.container>>; history: HistoryEvent[] } | null>(null);
  useDetailHistory(!!trace, () => { setTrace(null); });
  const [noHit, setNoHit] = useState(false);

  useEffect(() => {
    Api.bundle().then((b: ReferenceBundle) => { locationCodes = new Map(b.locations.map((l) => [l.id, l.code])); setEvents((e) => (e ? [...e] : e)); }).catch(() => undefined);
    Admin.events(0, 300, true).then((e) => setEvents([...e].reverse())).catch(() => setEvents([]));
    Admin.integrity().then(setIntegrity).catch(() => setIntegrity(null));
  }, []);

  const search = async () => {
    const q = query.trim();
    if (!q) return;
    setNoHit(false);
    let rows: ContainerSummary[] = [];
    if (/^[0-9a-f-]{36}$/i.test(q)) {
      try {
        const c = await Admin.container(q);
        await openTrace(c.container_id);
        return;
      } catch {
        rows = [];
      }
    } else if (/^\d{18}$/.test(q)) rows = await Api.containersByIdentity(`sscc:00=${q}`);
    else if (/^\d{8,14}$/.test(q)) rows = await Api.containersByIdentity(`gtin+lot:01=${q.padStart(14, "0")};10=`).catch(() => []);
    if (rows.length === 0) {
      // Fall back to the stock rows: lot or GTIN match.
      const stock = await Admin.stock();
      const hit = stock.find((r) => r.lot_number === q || r.sscc === q || (r.gtin && r.gtin.endsWith(q)));
      if (!hit) {
        setNoHit(true);
        return;
      }
      await openTrace(hit.container_id);
      return;
    }
    await openTrace(rows[0]!.container_id);
  };

  const openTrace = async (containerId: string) => {
    const [container, history] = await Promise.all([Admin.container(containerId), Admin.history(containerId)]);
    setTrace({ container, history });
  };

  const download = (content: unknown, name: string, type: string) => {
    const blob = new Blob([JSON.stringify(content, null, 2)], { type });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
  };
  const exportJson = () => download(trace ? trace.history : events, trace ? `trail-trace-${trace.container.container_id}.json` : "trail-events.json", "application/json");
  const exportEpcis = async () => {
    const doc = trace ? await Admin.epcisForContainer(trace.container.container_id) : await Admin.epcis();
    download(doc, trace ? `trail-epcis-${trace.container.container_id}.jsonld` : "trail-epcis.jsonld", "application/ld+json");
  };

  const c = trace?.container;
  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.ev.title")}</h1><div className="crumb">{t("adm.ev.sub", { n: events?.length ?? 0, status: integrity ? (integrity.ok ? t("adm.ev.ok") : t("adm.ev.bad")) : "…" })}</div></div>
        <div className="right">
          <form onSubmit={(e) => { e.preventDefault(); void search(); }} style={{ display: "flex", gap: 8 }}>
            <input className="ain" style={{ height: 36, width: 320, borderRadius: 10, border: "1px solid var(--line)", background: "var(--bg)", padding: "0 12px", font: "inherit", color: "var(--text)" }} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("adm.ev.search")} />
            <button type="submit" className="abtn">{t("adm.ev.trace")}</button>
          </form>
          <button type="button" className="abtn primary" onClick={() => void exportEpcis()}>{t("adm.ev.exportEpcis")}</button>
          <button type="button" className="abtn" onClick={exportJson}>{t("adm.ev.export")}</button>
        </div>
      </div>
      <div className="adm-content">
        {noHit && <p className="ahint" style={{ color: "var(--bad)" }}>{t("adm.ev.noHit")}</p>}
        <div className={`adm-cols${!!trace ? " detail-open" : ""}`}>
          {trace && c && (
            <div className="adm-w460 adm-detail adm-detail-only" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {!!trace && <DetailBar title={trace ? (trace.container.sscc ?? trace.container.lot_number ?? trace.container.container_id) : ""} sub={undefined} onBack={() => { setTrace(null); }} />}
              <div className="label" style={{ background: "var(--panel)" }}>
                <div className="head"><b>{c.kind === "logistic-unit" ? t("unit.pallet") : t("unit.kolli")}</b><span>{c.kind === "logistic-unit" && <button type="button" className="abtn sm" onClick={() => void Admin.printJob({ kind: "pallet-label", containerId: c.container_id }).then(() => setNoHit(false)).catch(() => undefined)}>{t("adm.ev.reprint")}</button>} {c.kind === "logistic-unit" && <button type="button" className="abtn sm" title={t("adm.print.viaAgent")} onClick={() => void printAndTell({ kind: "pallet-content", containerId: c.container_id }, t as never)}>{t("adm.ev.printContent")}</button>} {c.kind === "trade-item" && <button type="button" className="abtn sm" onClick={() => void printAndTell({ kind: "customer-label", containerId: c.container_id }, t as never)}>{t("adm.ev.printCustomerLabel")}</button>} {c.kind === "logistic-unit" && c.sscc && <button type="button" className="abtn sm" onClick={() => void printAndTell({ kind: "sscc-label", sscc: c.sscc ?? "" }, t as never)}>{t("adm.ev.printSscc")}</button>} <button type="button" className="abtn sm" onClick={() => setTrace(null)}>{t("common.close")}</button></span></div>
                <div className="rows">
                  {c.sscc && <div className="cell wide"><div className="t">SSCC</div><div className="v">{c.sscc}</div></div>}
                  {c.gtin && <div className="cell wide"><div className="t">GTIN</div><div className="v">{c.gtin}</div></div>}
                  {c.lot_number && <div className="cell"><div className="t">Batch/Lot</div><div className="v">{c.lot_number}</div></div>}
                  <div className="cell"><div className="t">{c.kind === "logistic-unit" ? "Count" : "Content"}</div><div className="v">{c.kind === "logistic-unit" ? `${c.children.length}` : fmtQty(c.quantity, c.uom)}</div></div>
                  <div className="cell wide"><div className="t">{t("adm.st.location")} · {t("adm.col.status")}</div><div className="v" style={{ fontFamily: "var(--sans)", fontSize: 16 }}>{loc(c.location_id)} · {(c as unknown as { state: string }).state}{(c as unknown as { intact: boolean }).intact ? " · intakt" : ""}</div></div>
                </div>
              </div>
              {c.children.length > 0 && (
                <div className="card"><div className="hd">{t("adm.ev.contents")} <span className="sub">{c.children.length}</span></div><div className="bd flush"><table className="at"><tbody>
                  {c.children.slice(0, 20).map((k) => <tr key={k.container_id} className="click" onClick={() => void openTrace(k.container_id)}><td className="id">{k.lot_number ?? k.gtin ?? k.container_id.slice(0, 8)}</td><td className="num">{fmtQty(k.quantity, k.uom)}</td></tr>)}
                  {c.children.length > 20 && <tr><td colSpan={2} className="mut">{t("rows.more", { n: c.children.length - 20 })}</td></tr>}
                </tbody></table></div></div>
              )}
              <div className="card"><div className="hd">{t("adm.ev.history")}</div><div className="bd">
                <ul className="timeline" style={{ listStyle: "none", margin: 0, padding: "0 0 0 16px", borderLeft: "2px solid var(--line)" }}>
                  {[...trace.history].reverse().map((e) => (
                    <li key={e.sequence} style={{ padding: "0 0 10px 10px" }}>
                      <div className="id mut" style={{ fontSize: 12 }}>{fmt(e.recordedAt)} · #{e.sequence} · {e.type}</div>
                      <div>{subjectOf(e)} · {e.actorId.replace(/^user:/, "")}{e.deviceId ? ` · ${e.deviceId}` : ""}{e.flowId ? ` · ${e.flowId} v${e.flowVersion}` : ""}{e.captureDetail ? ` · ${e.captureDetail}` : ""}</div>
                    </li>
                  ))}
                </ul>
              </div></div>
            </div>
          )}
          <div className="card adm-grow">
            <div className="bd flush">
              <table className="at">
                <thead><tr><th>{t("adm.ev.col.seq")}</th><th>{t("adm.col.when")}</th><th>{t("adm.ev.col.type")}</th><th>{t("adm.ev.col.subject")}</th><th>{t("adm.ev.col.by")}</th><th>{t("adm.ev.col.flow")}</th></tr></thead>
                <tbody>
                  {events?.map((e) => (
                    <tr key={e.eventId} className="click" onClick={() => { const id = (e.payload["containerId"] ?? e.payload["parentContainerId"] ?? (e.payload["containerIds"] as string[] | undefined)?.[0]) as string | undefined; if (id) void openTrace(id); }}>
                      <td className="id mut">{e.sequence}</td>
                      <td className="id mut">{fmt(e.recordedAt)}</td>
                      <td>{e.type}</td>
                      <td>{subjectOf(e)}</td>
                      <td className="mut">{e.actorId.replace(/^user:/, "")}</td>
                      <td className="mut">{e.flowId ? `${e.flowId} v${e.flowVersion}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
